import { isIosCompanionApp } from "@/lib/native/runtime";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { useStoreSubscriptionQuery } from "@/shared/subscriptions/services/useStoreSubscriptionQuery";

export type CustomerLockCopy = {
  /** Short placeholder that stands in for the buyer's name. */
  label: string;
  /** One calm sentence on what unlocks it. Detail surfaces only. */
  hint: string;
  /**
   * Placeholder for the ORDER itself — its items, its total, everything the
   * lock now covers beyond the buyer's identity.
   */
  orderLabel: string;
  /** The order-scoped version of {@link hint}. Detail surfaces only. */
  orderHint: string;
  /** Stands in for the money on a row that must not show a total. */
  amountLabel: string;
};

/**
 * The ONE place that decides how a masked order is explained to the seller.
 *
 * `customerInfoLocked` arrives on the record without saying WHY, because the
 * backend resolves that once per request (`OrderVisibilityPolicy`) and the flag
 * is the same in both modes. The reason therefore has to come from the store's
 * own billing state:
 *
 *  - store-level restriction — the subscription is unpaid, everything is masked
 *    and every write is blocked, so "pay to see" is the honest sentence;
 *  - per-order plan — the store is working normally and this one order's charge
 *    is simply not covered yet, so the details are HELD, not withheld as a
 *    penalty. They unlock oldest-first the moment the wallet is topped up.
 *
 * Restriction wins when both are somehow true: it is the harder stop, and a
 * restricted store cannot act on the wallet advice anyway.
 *
 * This is a bill the seller owes — never the buyer's doing — so no string here
 * blames the customer, and none of them reads as an error.
 *
 * The lock covers the order's CONTENTS as well as its buyer (item names, the
 * total, the address — see `mask-order.helper.ts` on the backend), so the copy
 * comes in two scopes: `label`/`hint` stand in for the person, and
 * `orderLabel`/`orderHint` for the order.
 */
export function useCustomerLockCopy(): CustomerLockCopy {
  const t = useTranslations();
  const storeSlug = useSellerStoreSlug();
  const { data } = useStoreSubscriptionQuery({ storeSlug });

  // A missing/unloaded status reads as NOT per-order, which falls back to the
  // long-standing restricted copy rather than promising a top-up that may not
  // apply to this store.
  const perOrderAmount = data?.wallet?.perOrderAmount ?? null;
  const isPerOrder = perOrderAmount !== null && !data?.isRestricted;

  const amountLabel = t(keys.seller.orderTotalHeld);

  if (isIosCompanionApp()) return {
    label: t(keys.seller.customerInfoLocked),
    hint: t(keys.iosCompanion.orderHeld),
    orderLabel: t(keys.seller.orderContentsLocked),
    orderHint: t(keys.iosCompanion.orderHeld),
    amountLabel,
  };

  return isPerOrder
    ? {
        label: t(keys.seller.customerInfoHeld),
        hint: t(keys.seller.customerInfoHeldHint),
        orderLabel: t(keys.seller.orderContentsHeld),
        orderHint: t(keys.seller.orderContentsHeldHint),
        amountLabel,
      }
    : {
        label: t(keys.seller.customerInfoLocked),
        hint: t(keys.seller.subscriptionRestrictedMessage),
        orderLabel: t(keys.seller.orderContentsLocked),
        orderHint: t(keys.seller.subscriptionRestrictedMessage),
        amountLabel,
      };
}
